import { ComponentProvider } from '@atoms/ComponentProvider';
import { markComponentKind } from '@atoms/util';
import { usePipelineStatus } from '@common/git/CodeGeneration/hook';
import { ProjectContext } from '@common/git/Project/context';
import { useSetIdeAction } from '@common/ide/state';
import { Entity, IDEEntity } from '@common/types/Entity';
import { useWorkflowExecutionState, WorkflowExecutionProvider } from '@common/workflowExecutionHooks';
import { BaseLSPProvider } from '@LSP/base/BaseLSPContext';
import { DiagnosticProvider } from '@LSP/DiagnosticProvider';
import { useCreateLSPClient } from '@LSP/hook';
import { PipelineLSPClient } from '@LSP/pipeline/client';
import { useOnce, useOnMount } from '@prophecy/utils/react/hooks';
import { nanoid } from 'nanoid';
import { useContext, useEffect } from 'react';
import { Provider } from 'react-redux';
import { createGlobalStyle } from 'styled-components';

import { useGlobalVariables } from '../../../context/globalConfig/securedConfigHook';
import { useExecutionService } from '../../../data/executionHooks';
import { LogProvider, useLoggerState } from '../../../generic-components/Logger/RuntimeLogger';
import { getWorkflowEditorWSEndPoint } from '../../../utils/getServerUrl';
import { isWebsocketCompressionEnabled } from '../../../utils/localstorage-keys';
import { ComponentRunContext, useRunDataContext } from '../../Workflow/AttachFabric/componentRunhook';
import { useWorkflowEditor } from '../../Workflow/hook';
import WorkflowUploadProvier from '../../Workflow/Upload/context';
import { WorkflowDialogFooter, WorkflowDialogTitle } from '../../Workflow/WorkflowDialog';
import { PipelineEntityType } from '../config';
import { useImportState } from '../Import/useImportState';
import { Container } from './MockContainer';
import { MockIDE } from './MockIDE';
import { MockWorkflowContext } from './mockWorkFlowContext';
import { MockWorkflowDialog, TranspilerDataset, TranspilerNewDataset } from './TranspilerDataset';

import type { PipelineInitializeParams } from '../../../common/ide/PipelineInitializeParamsTypes';

interface WorkflowProps {
  uid?: string;
  isUIDFromAnotherProject?: boolean;
  hasCodeGeneration?: boolean;
  isHistoryEnabled?: boolean;
  version?: string;
  configurationInstanceId?: string;
  projectId: string;
  projectName?: string;
  language: 'scala' | 'python' | 'sql';
  branch: string;
  fabricId?: string;
}

const OverrideComponents = {
  ...markComponentKind({
    'Dialogs.Container': MockWorkflowDialog,
    'Dialogs.Footer': WorkflowDialogFooter,
    'Dialogs.Title': WorkflowDialogTitle,
    Dataset: TranspilerDataset,
    NewDataset: TranspilerNewDataset
  })
};

const GlobalIDECSS = createGlobalStyle`
	html, body{
	/*To remove the mac gestures to go back to the previous page*/
  overscroll-behavior-x: none;
	}
`;

const websocketUrl = getWorkflowEditorWSEndPoint();

const MockPipeline = function Pipeline({
  uid,
  projectId,
  projectName,
  language,
  branch,
  hasCodeGeneration,
  fabricId
}: Required<WorkflowProps>) {
  const loggerState = useLoggerState();
  const isRebuildEnabled = useGlobalVariables().isCodeGenerationEnabled;
  const { isCodeGenerating, isCodeGenerationInProgress } = usePipelineStatus(uid, hasCodeGeneration);

  const IsCodeGenerationInProgress = Boolean(isCodeGenerating || isCodeGenerationInProgress);
  const { lspClient } = useCreateLSPClient(() => {
    return new PipelineLSPClient<PipelineInitializeParams>(websocketUrl, {
      compressionEnabled: isWebsocketCompressionEnabled,
      blockCodeView: true,
      lazyInit: IsCodeGenerationInProgress
    });
  });

  const { setIDEActions } = useSetIdeAction();

  const executionService = useExecutionService();

  useEffect(() => {
    if (!IsCodeGenerationInProgress && isRebuildEnabled) {
      lspClient.lazyInit();
    }
  }, [IsCodeGenerationInProgress, lspClient, isRebuildEnabled]);

  const { workflowStore } = useWorkflowEditor({
    lspClient,
    workflowId: uid,
    isTranspilerDatasetPreview: true,
    fabricId
  });

  const workflowExecutionState = useWorkflowExecutionState(
    uid,
    executionService,
    lspClient,
    loggerState.logger,
    loggerState.logs
  );

  useEffect(() => {
    setIDEActions({
      reinitializeLSPconnection: lspClient.reconnect
    });
  }, [setIDEActions, lspClient]);

  useEffect(() => {
    return () => {
      lspClient.detach();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runDataContext = useRunDataContext();

  const mockWorkflowMethods = useContext(MockWorkflowContext);

  useOnMount(() => {
    mockWorkflowMethods.callbackRef.current!.getWorkflowStore = () => workflowStore.getState();
  });

  return (
    <BaseLSPProvider client={lspClient}>
      <DiagnosticProvider>
        <Provider store={workflowStore}>
          <GlobalIDECSS />
          <WorkflowExecutionProvider value={workflowExecutionState}>
            <LogProvider value={loggerState}>
              <WorkflowUploadProvier>
                <ComponentProvider value={OverrideComponents}>
                  <ComponentRunContext.Provider value={runDataContext}>
                    <Container
                      uid={uid}
                      projectId={projectId}
                      projectName={projectName}
                      language={language}
                      branch={branch}
                    />
                  </ComponentRunContext.Provider>
                </ComponentProvider>
              </WorkflowUploadProvier>
            </LogProvider>
          </WorkflowExecutionProvider>
        </Provider>
      </DiagnosticProvider>
    </BaseLSPProvider>
  );
};

interface MockWorkflowProps {
  hasCodeGeneration?: boolean;
  configurationInstanceId?: string;
  currentPipelines: PipelineEntityType[];
}

export const MockPipelineComponent = ({
  hasCodeGeneration = false,
  configurationInstanceId,
  currentPipelines
}: MockWorkflowProps) => {
  const {
    formDataWithActions: {
      formContext: { getValues }
    }
  } = useImportState();

  const projectId = getValues('projectId');
  const branch = getValues('branch');
  const projectName = getValues('name');
  const language = getValues('language');
  const fabricId = getValues('fabricId');
  const newId = useOnce(nanoid);
  const uid = `${projectId}/pipelines/${currentPipelines[0]?.name || newId}`;

  const envVars = {
    uid,
    entity: Entity.Pipeline as IDEEntity,
    branch,
    'run-id': '',
    projectId,
    action: '',
    projectName,
    language,
    repositoryPath: '',
    path: '',
    fabricId
  };

  const mockProjectState = {
    isGitInfoLoading: false,
    isAdminLoading: false,
    isLoading: false,
    hasAnalysisProject: false,
    mainBranch: branch,
    projectName,
    currentBranch: branch,
    language,
    forkMode: undefined,
    unCommittedCount: 0,
    projectId,
    hasProjectDataFetchEnabled: true,
    isProjectAdmin: true,
    isSqlProject: false,
    isMainBranch: true,
    externalSubPath: '',
    isSimpleVersionControl: false,
    isExampleProject: false,
    industry: undefined,
    projectType: undefined,
    defaultTransformationEntity: undefined,
    currentProjectStatus: undefined,
    backendLanguage: language,
    teamId: undefined,
    harmonizationProperties: null,
    teamName: undefined,
    createdByFullName: undefined
  };

  return (
    <ProjectContext.Provider value={mockProjectState}>
      <MockIDE {...envVars}>
        <MockPipeline
          uid={uid}
          projectId={projectId}
          projectName={projectName}
          language={language}
          branch={branch}
          hasCodeGeneration={hasCodeGeneration}
          configurationInstanceId={configurationInstanceId as string}
          isUIDFromAnotherProject={false}
          isHistoryEnabled={false}
          version=''
          fabricId={fabricId}
        />
      </MockIDE>
    </ProjectContext.Provider>
  );
};
